"use client"

import { Link } from "react-router-dom"
import { Linkedin, Instagram, Twitter, Youtube, ArrowUpRight, MapPin, Phone } from "lucide-react"
import { useEffect, useRef, useState } from "react"

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const footerRef = useRef(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.08 }
    )
    if (footerRef.current) observer.observe(footerRef.current)
    return () => observer.disconnect()
  }, [])

  const usefulLinks = [
    { label: "Our Courses", href: "/#courses" },
    { label: "About Our Academy", href: "/about" },
    { label: "Our Gallery", href: "/results" },
    { label: "Read Our Blogs", href: "/blogs" },
  ]

  const information = [
    { label: "Our Director", href: "/about#director" },
    { label: "Contact Us", href: "/contact" },
    { label: "Talk to Rohith Sir", href: "/contact" },
    { label: "Privacy Policy", href: "/privacy" },
  ]

  const socials = [
    { Icon: Linkedin, href: "#", label: "LinkedIn" },
    { Icon: Instagram, href: "#", label: "Instagram" },
    { Icon: Twitter, href: "#", label: "Twitter" },
    { Icon: Youtube, href: "#", label: "YouTube" },
  ]

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        /* ── Keyframes ─────────────────────────────────────── */
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes lineGrow {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes wm-shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes wm-fadein {
          from { opacity: 0; letter-spacing: 0.35em; }
          to   { opacity: 1; letter-spacing: 0.22em; }
        }
        @keyframes goldPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(180,140,54,0); }
          50%       { box-shadow: 0 0 16px 2px rgba(180,140,54,0.18); }
        }

        /* ── Base ──────────────────────────────────────────── */
        .raf * { box-sizing: border-box; margin: 0; padding: 0; }

        .raf {
          position: relative;
          background: #080b18;
          padding-bottom: clamp(60px, 10vw, 140px);
          overflow: hidden;
          font-family: 'DM Sans', sans-serif;
        }

        /* ── Decorative top rule ───────────────────────────── */
        .raf-topline {
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 1px;
          transform-origin: left;
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(180,140,54,0.3) 20%,
            #b48c36 50%,
            rgba(180,140,54,0.3) 80%,
            transparent 100%
          );
          animation: lineGrow 1.2s cubic-bezier(0.4,0,0.2,1) 0.2s both;
        }

        /* ── Ambient glows ─────────────────────────────────── */
        .raf-glow-blue {
          position: absolute;
          bottom: -80px; right: -80px;
          width: 560px; height: 560px;
          background: radial-gradient(ellipse at center,
            rgba(37,99,235,0.09) 0%,
            transparent 65%
          );
          pointer-events: none;
        }

        /* ── WATERMARK ─────────────────────────────────────── */
        .raf-wm {
          position: absolute;
          bottom: 12px;
          left: 50%;
          transform: translateX(-50%);
          pointer-events: none;
          user-select: none;
          z-index: 0;
          white-space: nowrap;
          text-align: center;
          width: 100%;
          overflow: hidden;
          padding: 0 8px;
        }
        .raf-wm-line {
          display: block;
          font-family: 'Cormorant Garamond', serif;
          font-weight: 700;
          /* Substantial reduction for a professional look */
          font-size: clamp(24px, 8vw, 84px);
          letter-spacing: 0.15em;
          text-transform: uppercase;
          line-height: 0.95;
          white-space: nowrap;
          /* balanced visibility shimmer */
          background: linear-gradient(
            110deg,
            rgba(180,140,54,0.08) 0%,
            rgba(180,140,54,0.25) 25%,
            rgba(217,176,80,0.35) 50%,
            rgba(180,140,54,0.25) 75%,
            rgba(180,140,54,0.08) 100%
          );
          background-size: 250% auto;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          -webkit-text-stroke: 0px transparent;
          animation: wm-fadein 1.2s ease 0.4s both,
                     wm-shimmer 4s linear 1.6s infinite;
        }


        /* ── Inner wrapper ─────────────────────────────────── */
        .raf-inner {
          position: relative;
          z-index: 1;
          max-width: 1160px;
          margin: 0 auto;
          padding: 0 clamp(20px, 4vw, 44px) clamp(48px, 8vw, 100px);
        }

        /* ── TOP BAR ───────────────────────────────────────── */
        .raf-topbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 38px 0 34px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          gap: 24px;
          flex-wrap: wrap;
          opacity: 0;
          animation: ${visible ? 'fadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s forwards' : 'none'};
        }
        .raf-brand {
          display: flex;
          align-items: center;
          gap: 13px;
          text-decoration: none;
        }
        .raf-logo {
          width: 72px; height: 72px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          transition: transform 0.3s;
          overflow: hidden;
        }
        .raf-logo:hover { transform: scale(1.05); }
        .raf-logo img { width: 100%; height: 100%; object-cover: cover; }
        .raf-brand-name {
          display: block;
          font-family: 'Cormorant Garamond', serif;
          font-weight: 700;
          font-size: clamp(18px, 2.2vw, 22px);
          color: #fff;
          letter-spacing: 0.01em;
          line-height: 1;
        }
        .raf-brand-copyright {
          display: block;
           font-family: 'DM Sans', sans-serif;
           font-weight: 500; font-size: 11px;
           color: rgba(255,255,255,0.5);
           letter-spacing: 0.05em;
          margin-top: 4px;
          margin-bottom: 2px;
        }
        .raf-brand-sub {
          display: block;
           font-family: 'DM Sans', sans-serif;
           font-weight: 700; font-size: 11px;
           color: #b48c36;
           letter-spacing: 0.3em;
          text-transform: uppercase;
          margin-top: 4px;
        }

        .raf-chips { display: flex; gap: 28px; align-items: center; flex-wrap: wrap; }
        .raf-chip {
          display: flex; align-items: center; gap: 9px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px; font-weight: 400;
          color: rgba(255,255,255,0.42);
          transition: color 0.2s;
          cursor: default;
        }
        .raf-chip:hover { color: rgba(255,255,255,0.7); }
        .raf-chip-icon {
          width: 28px; height: 28px;
          border-radius: 50%;
          border: 1px solid rgba(180,140,54,0.22);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          transition: border-color 0.2s;
        }
        .raf-chip:hover .raf-chip-icon { border-color: rgba(180,140,54,0.5); }

        /* ── MAIN GRID ─────────────────────────────────────── */
        /* 4 equal(ish) columns — hero gets 1.7fr */
        .raf-grid {
          display: grid;
          grid-template-columns: 1.7fr 1fr 1fr 1fr;
          gap: 0;
          padding: 56px 0 52px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          align-items: flex-start;
        }

        /* Dividers between columns */
        .raf-grid > div:not(:first-child) {
          border-left: 1px solid rgba(255,255,255,0.05);
          padding-left: 36px;
        }
        .raf-grid > div:not(:last-child) {
          padding-right: 36px;
        }

        /* Hero CTA */
        .raf-hero {
          opacity: 0;
          animation: ${visible ? 'fadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.25s forwards' : 'none'};
        }
        .raf-hero-heading {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(26px, 2.6vw, 38px);
          font-weight: 400;
          color: #fff;
          line-height: 1.18;
          letter-spacing: -0.01em;
          margin-bottom: 16px;
          /* Prevent awkward orphan wrapping */
          white-space: nowrap;
        }
        @media (max-width: 1060px) { .raf-hero-heading { white-space: normal; } }
        .raf-hero-heading em {
          font-style: ;
          font-weight: 300;
          color: #b48c36;
        }
        .raf-hero-sub {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 300;
          color: rgba(255,255,255,0.38);
          line-height: 1.75;
          margin-bottom: 30px;
          max-width: 290px;
        }
        .raf-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 10.5px; font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #fff;
          text-decoration: none;
          position: relative;
          padding-bottom: 4px;
          transition: color 0.25s;
        }
        .raf-cta::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0;
          width: 100%; height: 1px;
          background: #b48c36;
          transform-origin: right;
          transform: scaleX(0.4);
          transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
          opacity: 0.5;
        }
        .raf-cta:hover { color: #b48c36; }
        .raf-cta:hover::after { transform: scaleX(1); opacity: 1; }
        .raf-cta svg { transition: transform 0.25s; }
        .raf-cta:hover svg { transform: translate(2px, -2px); }

        /* Nav columns */
        .raf-col {
          opacity: 0;
        }
        .raf-col:nth-child(2) { animation: ${visible ? 'fadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.35s forwards' : 'none'}; }
        .raf-col:nth-child(3) { animation: ${visible ? 'fadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.45s forwards' : 'none'}; }
        .raf-col:nth-child(4) { animation: ${visible ? 'fadeUp 0.7s cubic-bezier(0.4,0,0.2,1) 0.55s forwards' : 'none'}; }

        .raf-col-label {
          display: block;
          font-family: 'DM Sans', sans-serif;
          font-size: 8.5px; font-weight: 600;
          color: #b48c36;
          letter-spacing: 0.3em;
          text-transform: uppercase;
          margin-bottom: 24px;
        }

        .raf-nav-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .raf-nav-link {
           font-family: 'DM Sans', sans-serif;
           font-size: 14px; font-weight: 400;
           color: rgba(255,255,255,0.7);
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: color 0.2s, gap 0.2s;
          position: relative;
        }
        .raf-nav-link::before {
          content: '';
          width: 0; height: 1px;
          background: #b48c36;
          transition: width 0.25s;
          flex-shrink: 0;
        }
        .raf-nav-link:hover { color: #fff; gap: 10px; }
        .raf-nav-link:hover::before { width: 14px; }

        /* Socials */
        .raf-social-desc {
          display: block;
           font-family: 'DM Sans', sans-serif;
           font-size: 13px; font-weight: 500;
           color: rgba(255,255,255,0.5);
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .raf-socials {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          gap: 8px;
        }
        .raf-social-btn {
          width: 34px; height: 34px;
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,0.32);
          text-decoration: none;
          flex-shrink: 0;
          transition: all 0.25s;
          animation: ${visible ? 'goldPulse 2.5s ease-in-out infinite' : 'none'};
        }
        .raf-social-btn:nth-child(1) { animation-delay: 0s; }
        .raf-social-btn:nth-child(2) { animation-delay: 0.5s; }
        .raf-social-btn:nth-child(3) { animation-delay: 1s; }
        .raf-social-btn:nth-child(4) { animation-delay: 1.5s; }
        .raf-social-btn:hover {
          border-color: #b48c36;
          color: #b48c36;
          background: rgba(180,140,54,0.08);
          transform: translateY(-2px);
          animation: none;
          box-shadow: 0 4px 16px rgba(180,140,54,0.2);
        }

        /* ── BOTTOM BAR ────────────────────────────────────── */
        .raf-bottom {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 12px;
          padding: 16px 0 20px;
          opacity: 0;
          animation: ${visible ? 'fadeIn 0.8s ease 0.65s forwards' : 'none'};
        }
        .raf-copy {
          font-family: 'DM Sans', sans-serif;
          font-size: 9.5px; font-weight: 500;
          color: rgba(255,255,255,0.35);
          letter-spacing: 0.17em;
          text-transform: uppercase;
        }
        .raf-tagline {
          font-family: 'DM Sans', sans-serif;
          font-size: 9.5px; font-weight: 500;
          letter-spacing: 0.17em;
          text-transform: uppercase;
          color: rgba(190,18,60,0.45);
        }

        /* ── Responsive ────────────────────────────────────── */
        @media (max-width: 940px) {
          .raf-grid {
            grid-template-columns: 1fr 1fr;
            gap: 36px;
          }
          .raf-grid > div:not(:first-child) {
            border-left: none;
            padding-left: 0;
          }
          .raf-grid > div:not(:last-child) {
            padding-right: 0;
          }
          .raf-hero { grid-column: 1 / -1; }
          .raf-hero-heading { white-space: normal; }
        }
        @media (max-width: 560px) {
          .raf-grid { grid-template-columns: 1fr; }
          .raf-inner { padding: 0 24px; }
          .raf-chips { gap: 16px; }
          .raf-topbar { flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <footer className="raf" ref={footerRef}>

        {/* Ghost outline watermark — single line */}
        <div className="raf-wm" aria-hidden="true">
          <span className="raf-wm-line">Rohith'S ACADEMY</span>
        </div>

        {/* Ambient colour glows */}
        <div className="raf-glow-blue" />

        {/* Animated top accent rule */}
        <div className="raf-topline" />

        <div className="raf-inner">

          {/* ── Brand bar ── */}
          <div className="raf-topbar">
            <Link to="/" className="raf-brand">
              <div className="raf-logo">
                <img src="/logo-final.png" alt="Rohith's Academy" />
              </div>
              <div>
                <span className="raf-brand-name">Rohith's Academy</span>
                <span className="raf-brand-copyright">© 2026 Rohith's Academy · All Rights Reserved</span>
                <span className="raf-brand-sub">TSPSC · APPSC Excellence</span>
              </div>
            </Link>

            <div className="raf-chips">
              <div className="raf-chip">
                <div className="raf-chip-icon">
                  <MapPin size={12} color="#b48c36" />
                </div>
                Ashok Nagar, Hyderabad
              </div>
              <div className="raf-chip">
                <div className="raf-chip-icon">
                  <Phone size={12} color="#b48c36" />
                </div>
                +91 98765 43210
              </div>
            </div>
          </div>

          {/* ── Main content grid ── */}
          <div className="raf-grid">

            {/* Hero CTA */}
            <div className="raf-hero">
              <h2 className="raf-hero-heading">
                Do you like <em>what you see?</em>
              </h2>
              <p className="raf-hero-sub">
                Let's build the future IAS — together. Step into the ranks of Telangana's finest civil servants.
              </p>
              <Link to="/contact" className="raf-cta">
                Get in Touch with Rohith Sir
                <ArrowUpRight size={13} />
              </Link>
            </div>

            {/* Useful Links */}
            <div className="raf-col">
              <span className="raf-col-label">Useful Links</span>
              <ul className="raf-nav-list">
                {usefulLinks.map((l) => (
                  <li key={l.label}>
                    <Link to={l.href} className="raf-nav-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Information */}
            <div className="raf-col">
              <span className="raf-col-label">Information</span>
              <ul className="raf-nav-list">
                {information.map((l) => (
                  <li key={l.label}>
                    <Link to={l.href} className="raf-nav-link">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Get in Touch */}
            <div className="raf-col">
              <span className="raf-col-label">Get in Touch</span>
              <span className="raf-social-desc">Connect with us<br />via our socials</span>
              <div className="raf-socials">
                {socials.map(({ Icon, href, label }) => (
                  <a key={label} href={href} aria-label={label} className="raf-social-btn">
                    <Icon size={14} strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>

          </div>

          {/* ── Copyright bar ── */}
          <div className="raf-bottom">
            <span className="raf-copy">© {currentYear} Rohith's Academy · All Rights Reserved</span>
          </div>

        </div>
      </footer>
    </>
  )
}

export default Footer

